import { PureComponent } from 'react';
import Routes from 'ContentUtils/Routes';
import styled from 'styled-components';
import { filterProps } from 'foundations-components/utils/filterProps';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import UIIcon from 'UIComponents/icon/UIIcon';
import { DEFAULT_WIDGET_ICON, WIDGET_ICON_MAP } from 'ContentComponents/constants/Icons';
// @ts-expect-error no export yet
import UrlPattern from 'PatternValidationJS/patterns/Url';
import { ICON_SETS } from 'ContentUtils/constants/IconSets';
import withTheme from 'ContentComponents/cmv2/decorators/withTheme';
import { jsx as _jsx } from "react/jsx-runtime";
const {
  getContentIconsUrl
} = Routes;
const DEFAULT_SIZE = 'xxs';
export const iconFetchCache = {};
const ALIGNMENT_VALUES = ['baseline', 'sub', 'super', 'text-top', 'text-bottom', 'middle', 'top', 'bottom', 'inherit', 'initial'];
const ICON_SIZES = {
  small: 16,
  medium: 32,
  large: 48,
  xxs: 16,
  xs: 24,
  sm: 48,
  md: 64,
  lg: 72,
  xl: 96,
  xxl: 128
};
const IconWrapper = styled.div.withConfig({
  displayName: "WidgetIcon__IconWrapper",
  componentId: "sc-1chl2j9-0"
})(({
  size,
  alignment,
  cssStyles
}) => {
  return {
    '&&&&': Object.assign({}, cssStyles, {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      verticalAlign: alignment || 'middle',
      width: size,
      height: size
    })
  };
});
const IconMask = styled.div.withConfig({
  displayName: "WidgetIcon__IconMask",
  componentId: "sc-1chl2j9-1"
})(({
  color,
  iconUrl,
  size
}) => {
  const iconUrlEscapedParenz = iconUrl === null || iconUrl === void 0 ? void 0 : iconUrl.replace(/(?=[()])/g, '\\');
  const maskStyle = `url(${iconUrlEscapedParenz}) no-repeat center/contain`;
  return {
    '&&&&': {
      width: size,
      height: size,
      backgroundColor: color,
      mask: maskStyle,
      WebkitMask: maskStyle
    }
  };
});
const withCssStyles = Component => {
  return props => {
    const {
      tokenProps
    } = filterProps(props);
    const cssStyles = useMapTokenPropsToCSS(tokenProps);
    return /*#__PURE__*/_jsx(Component, Object.assign({}, props, {
      cssStyles: cssStyles
    }));
  };
};
class WidgetIcon extends PureComponent {
  constructor(props) {
    super(props);
    this.checkIconUrl = this.checkIconUrl.bind(this);
    this.onIconFetchComplete = this.onIconFetchComplete.bind(this);
    this.img = null;
    this._isMounted = false;
    this.state = {
      iconFetchComplete: {}
    };
  }
  componentDidMount() {
    this._isMounted = true;
    this.checkIconUrl();
  }
  componentDidUpdate(prevProps) {
    const {
      icon
    } = this.props;
    if (prevProps.icon !== icon) this.checkIconUrl();
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  get iconUrl() {
    const {
      icon
    } = this.props;
    if (!icon) return icon;
    if (UrlPattern.test(icon)) return icon;
    // If not a URL, icon be formatted as name, name:style, set:name:style, or set:name
    const iconProperties = icon.split(':');
    let iconName;
    let iconStyle;
    let iconSet;
    if (ICON_SETS[iconProperties[0]]) {
      // When set is explicitly stated (supports set:name:style or set:name)
      iconSet = iconProperties[0];
      iconName = iconProperties[1];
      iconStyle = iconProperties[2];
    } else {
      // When no set is provided it defaults to fontawesome-5.0.10 (supports name:style or name on its own)
      iconName = iconProperties[0];
      iconStyle = iconProperties[1];
    }
    return getContentIconsUrl(iconSet, iconName, iconStyle);
  }
  checkIconUrl() {
    const {
      iconUrl
    } = this;
    if (!iconUrl) return;
    if (!iconFetchCache[iconUrl]) {
      const img = new Image();
      /*
      Some icon URLs return a CORs error within a sidebar but still trigger
      onload which leads to a blank icon. This sets the image to fetch using CORs
      with credentials='same-origin', so it ensures that onerror gets triggered
      for these URLs so it'll use the fallback
      */
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.onIconFetchComplete(iconUrl, false);
      };
      img.onerror = () => {
        this.onIconFetchComplete(iconUrl, true);
      };
      img.src = iconUrl;
      this.img = img;
    }
  }
  onIconFetchComplete(iconUrl, failed) {
    iconFetchCache[iconUrl] = {
      failed
    };
    if (this._isMounted) {
      this.setState({
        iconFetchComplete: Object.assign({}, this.state.iconFetchComplete, {
          [iconUrl]: true
        })
      });
    }
  }
  render() {
    const {
      alignment,
      cssStyles,
      size,
      theme,
      type
    } = this.props;
    const {
      iconUrl
    } = this;
    let effectiveSize = parseInt(`${ICON_SIZES[size] || size}`, 10);
    effectiveSize = isNaN(effectiveSize) ? parseInt(`${ICON_SIZES[DEFAULT_SIZE]}`, 10) : effectiveSize;
    const color = this.props.color || theme.color['icon-core-default'];
    let renderedIcon;
    const iconFetchFailed = iconFetchCache[iconUrl] && iconFetchCache[iconUrl].failed;
    const name = WIDGET_ICON_MAP[type] || DEFAULT_WIDGET_ICON;
    if (!iconUrl || iconFetchFailed) {
      renderedIcon = /*#__PURE__*/_jsx(UIIcon, {
        "data-test-id": name,
        color: color,
        name: name,
        size: size
      });
    } else {
      renderedIcon = /*#__PURE__*/_jsx(IconMask, {
        "data-test-id": "custom-iconUrl",
        size: effectiveSize,
        color: color,
        iconUrl: this.iconUrl
      });
    }
    return /*#__PURE__*/_jsx(IconWrapper, {
      alignment: alignment,
      "data-test-id": "widget-icon",
      size: effectiveSize,
      cssStyles: cssStyles,
      children: renderedIcon
    });
  }
}
WidgetIcon.defaultProps = {
  alignment: null,
  className: null,
  icon: null,
  size: DEFAULT_SIZE
};
export default withTheme(withCssStyles(WidgetIcon));